/**
 * @file preview.ts
 * @description Предпросмотр сайта в кабинете (phase19.16 Р1; SB51 — главная,
 * каталог, товар, корзина, оформление заказа, кабинет покупателя, блог на
 * 360/768/ПК; uiux-design-r1 §1 «Список экранов предпросмотра»).
 *
 * Экраны — реестром (OCP): новый экран = запись с адресом витрины. Адрес
 * «Товар» и «Любая страница» подставляется выбранным товаром или страницей.
 * Масштаб — чтобы сайт шириной 1280 поместился в колонку предпросмотра.
 */

/** Экран предпросмотра. */
export type PreviewScreenKey = "home" | "catalog" | "product" | "cart" | "checkout" | "account" | "blog" | "page";

export interface PreviewScreen {
  key: PreviewScreenKey;
  label: string;
  /** Адрес на витрине; `null` — подставляется выбранным товаром/страницей. */
  path: string | null;
  /** Экран с образцом данных (корзина, заказ, кабинет) — подпись под предпросмотром. */
  sample?: boolean;
}

/**
 * Реестр экранов. Корзина — открытая боковая панель витрины (`?cart=open`,
 * поддерживает витрина в режиме предпросмотра, шаг Р1-4).
 */
export const PREVIEW_SCREENS: readonly PreviewScreen[] = [
  { key: "home", label: "Главная", path: "/" },
  { key: "catalog", label: "Каталог", path: "/catalog" },
  { key: "product", label: "Товар", path: null },
  { key: "cart", label: "Корзина", path: "/?cart=open", sample: true },
  { key: "checkout", label: "Оформление заказа", path: "/checkout", sample: true },
  { key: "account", label: "Кабинет покупателя", path: "/lk", sample: true },
  { key: "blog", label: "Блог", path: "/blog" },
  { key: "page", label: "Любая страница…", path: null },
];

/**
 * Ширины предпросмотра: телефон, планшет, ПК (mobile-first набор) и широкий
 * экран 1920 — на нём видна рамка сайта шире 1280 и «Фон за рамку сайта»
 * (SB120 этап 2, uiux-sb120-stage2 §3, факт 2). Масштаб «по ширине», как у 1280.
 */
export const PREVIEW_WIDTHS = [360, 768, 1280, 1920] as const;
export type PreviewWidth = (typeof PREVIEW_WIDTHS)[number];

/** Что показать: черновик или опубликованный сайт. */
export type PreviewMode = "draft" | "live";

/** Адрес экрана: для товара и страницы — выбранный slug. */
export function screenPath(screen: PreviewScreen, slug: string | null): string {
  if (screen.path !== null) return screen.path;
  if (!slug) return screen.key === "product" ? "/catalog" : "/";
  return screen.key === "product" ? `/product/${slug}` : `/${slug}`;
}

/**
 * Адрес iframe черновика: `previewUrl` сервера (`…/site-preview?token=…
 * &path=/`) с нужным путём. Путь передаётся параметром, а не переходом
 * внутри iframe: на чужом домене куки сторонних сайтов браузер может не
 * сохранить, а токен в адресе работает всегда.
 */
export function draftFrameUrl(previewUrl: string, path: string): string {
  try {
    const url = new URL(previewUrl);
    url.searchParams.set("path", path);
    return url.toString();
  } catch {
    return previewUrl;
  }
}

/**
 * Р3а: «Посмотреть» версию страницы на канве — вход по подписанному ключу
 * предпросмотра с `pageId` и `version` (`/site-preview?token=…&pageId=…&version=N`);
 * витрина сама переводит на `/preview/revision/<pageId>/<N>` и рисует внизу
 * полосу «Версия N от … — так было на сайте» (web-storefront, Р3а).
 */
export function pageRevisionFrameUrl(previewUrl: string, pageId: string, version: number): string | null {
  try {
    const url = new URL(previewUrl);
    url.searchParams.delete("path");
    url.searchParams.set("pageId", pageId);
    url.searchParams.set("version", String(version));
    return url.toString();
  } catch {
    return null;
  }
}

/** Маршрут витрины для входа в предпросмотр и выхода из него (web, шаг Р1-4). */
export const SITE_PREVIEW_ROUTE = "/site-preview";

/**
 * Адрес «Сейчас на сайте» — опубликованная витрина. Идёт через выход из
 * предпросмотра (`/site-preview?exit=1&path=…`, контракт §7): токен
 * черновика лежит в cookie сайта, и без выхода тот же браузер показал бы
 * черновик вместо того, что видят покупатели. Маршрут витрины — не под
 * `/api/`: на проде всё под `/api/` общий nginx отдаёт NestJS.
 */
export function liveFrameUrl(siteUrl: string, path: string): string {
  const query = new URLSearchParams({ exit: "1", path });
  return `${siteUrl.replace(/\/+$/, "")}${SITE_PREVIEW_ROUTE}?${query.toString()}`;
}

/** Масштаб сайта в колонке: не больше 1 (360 — в натуральную величину). */
export function previewScale(containerWidth: number, siteWidth: number): number {
  if (containerWidth <= 0 || siteWidth <= 0) return 1;
  return Math.min(1, Math.floor((containerWidth / siteWidth) * 1000) / 1000);
}

/**
 * Предпросмотр на весь экран — без рамок и ярлыков канвы («Шапка · общая
 * для всех страниц»): витрина рисует их только в канве конструктора, а
 * `canvas=0` просит обычный вид (согласовано с витриной, phase19.16 Р1-6а).
 */
export function plainPreviewUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("canvas", "0");
    return parsed.toString();
  } catch {
    return url;
  }
}
